import { Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material'
import {
  Box,
  Button,
  Chip,
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid2 as Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import React, { useState } from 'react'

import { RELATIONS, SKILL_LEVELS } from '../../share/Constant'
import { RELATIONS_OPTIONS, SKILL_LEVELS_OPTIONS, SPORTS_OPTIONS, SKILL_LEVEL_TOOLTIP } from '../../utils/UIConstant'

/**
 * FamilyMemberForm - Component for managing family members
 * Allows users to add, edit, and remove family members from their profile
 * Supports the new OpenAPI spec with age, relation, phoneNumber, and sports with skill levels
 */
const FamilyMemberForm = ({ familyMembers = [], onChange, userFirstName = '', userLastName = '' }) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [editingIndex, setEditingIndex] = useState(-1)
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    age: '',
    relation: RELATIONS.OTHER,
    phoneNumber: '',
    sports: [],
  })
  const [formErrors, setFormErrors] = useState({})
  const [isAddSportOpen, setIsAddSportOpen] = useState(false)
  const [newSportValue, setNewSportValue] = useState('')
  const [newSportLevel, setNewSportLevel] = useState(SKILL_LEVELS.BEGINNER)

  const normalizeSportsData = (sports = []) => {
    return (sports || []).map((s) => {
      const upper = (s.skillLevel || '').toString().toUpperCase()
      const valid = Object.values(SKILL_LEVELS).includes(upper) ? upper : SKILL_LEVELS.BEGINNER
      return { name: s.name, skillLevel: valid }
    })
  }

  const handleOpenDialog = (index = -1) => {
    if (index >= 0) {
      // Editing existing family member
      setEditingIndex(index)
      const member = familyMembers[index]
      setFormData({
        firstName: member.firstName || '',
        lastName: member.lastName || '',
        age: member.age || '',
        relation: member.relation || 'OTHER',
        phoneNumber: member.phoneNumber || '',
        sports: normalizeSportsData(member.sports || []),
      })
    } else {
      // Adding new family member
      setEditingIndex(-1)
      setFormData({
        firstName: '',
        lastName: '',
        age: '',
        relation: 'OTHER',
        phoneNumber: '',
        sports: [],
      })
    }
    setFormErrors({})
    setIsDialogOpen(true)
  }

  const handleCloseDialog = () => {
    setIsDialogOpen(false)
    setEditingIndex(-1)
    setFormData({ firstName: '', lastName: '', age: '', relation: 'OTHER', phoneNumber: '', sports: [] })
    setFormErrors({})
  }

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }))
    // Clear error when user starts typing
    if (formErrors[field]) {
      setFormErrors((prev) => ({
        ...prev,
        [field]: '',
      }))
    }
  }

  const validateForm = () => {
    const errors = {}

    if (!formData.firstName.trim()) {
      errors.firstName = 'First name is required'
    }

    if (formData.age && (isNaN(formData.age) || formData.age < 0 || formData.age > 150)) {
      errors.age = 'Age must be a valid number between 0 and 150'
    }

    setFormErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleSave = () => {
    if (!validateForm()) {
      return
    }

    const updatedMembers = [...familyMembers]
    const memberData = {
      firstName: formData.firstName.trim(),
      lastName: formData.lastName.trim(),
      age: formData.age ? parseInt(formData.age, 10) : undefined,
      relation: formData.relation,
      phoneNumber: formData.phoneNumber.trim() || undefined,
      sports: formData.sports,
      // familyId: editingIndex >= 0 ? familyMembers[editingIndex].familyId : Date.now(), // Use timestamp as unique ID for new members
      familyId: editingIndex >= 0 ? familyMembers[editingIndex].familyId : familyMembers.length,
    }

    if (editingIndex >= 0) {
      // Update existing member
      updatedMembers[editingIndex] = memberData
    } else {
      // Add new member
      updatedMembers.push(memberData)
    }

    onChange(updatedMembers)
    handleCloseDialog()
  }

  const handleDelete = (index) => {
    const updatedMembers = familyMembers.filter((_, i) => i !== index)
    onChange(updatedMembers)
  }

  const handleRemoveSport = (sportName) => {
    setFormData((prev) => ({
      ...prev,
      sports: (prev.sports || []).filter((s) => s.name !== sportName),
    }))
  }

  const handleSkillLevelChange = (sportName, skillLevel) => {
    setFormData((prev) => ({
      ...prev,
      sports: prev.sports.map((sport) => (sport.name === sportName ? { ...sport, skillLevel } : sport)),
    }))
  }

  // Ensure account owner is always first and shows user's name
  const displayMembers = () => {
    const accountOwner = familyMembers.find((m) => m.relation === RELATIONS.SELF)
    const otherMembers = familyMembers.filter((m) => m.relation !== RELATIONS.SELF)

    // Create account owner entry if it doesn't exist
    const ownerEntry = accountOwner || {
      familyId: 0,
      firstName: userFirstName,
      lastName: userLastName,
      relation: RELATIONS_OPTIONS.SELF.value,
      age: undefined,
      phoneNumber: undefined,
      sports: [],
    }

    return [ownerEntry, ...otherMembers]
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" color="text.primary">
          Family Members
        </Typography>
      </Box>

      {displayMembers().length === 0 ? (
        <Paper sx={{ p: 2, textAlign: 'center' }}>
          <Typography color="text.secondary" variant="body2">
            No family members added yet. Click "Add Family Member" to get started.
          </Typography>
        </Paper>
      ) : (
        <Stack spacing={1}>
          {displayMembers().map((member, index) => {
            const isAccountOwner = member.relation === RELATIONS.SELF
            const relationshipLabel = RELATIONS_OPTIONS[member.relation]?.label || member.relation

            return (
              <Paper key={member.familyId || index} sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {member.firstName} {member.lastName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {relationshipLabel}
                      {member.age && ` • Age: ${member.age}`}
                      {member.phoneNumber && ` • ${member.phoneNumber}`}
                    </Typography>
                    {member.sports && member.sports.length > 0 && (
                      <Box sx={{ mt: 1 }}>
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                          {member.sports.map((sport, sportIndex) => (
                            <Chip key={sportIndex} label={`${sport.name} (${sport.skillLevel})`} size="small" variant="outlined" sx={{ fontSize: '0.75rem', height: 20 }} />
                          ))}
                        </Stack>
                      </Box>
                    )}
                  </Box>
                  <Box>
                    <IconButton size="small" onClick={() => handleOpenDialog(index)} sx={{ mr: 1 }}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    {!isAccountOwner && (
                      <IconButton size="small" color="error" onClick={() => handleDelete(index)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                </Box>
              </Paper>
            )
          })}
        </Stack>
      )}
      <Button startIcon={<AddIcon />} onClick={() => handleOpenDialog()} size="small" variant="outlined" sx={{ mt: 1 }}>
        Add Family Member
      </Button>

      {/* Add/Edit Family Member Dialog */}
      <Dialog open={isDialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>{editingIndex >= 0 ? 'Edit Family Member' : 'Add Family Member'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="First Name"
                value={formData.firstName}
                onChange={(e) => handleInputChange('firstName', e.target.value)}
                error={!!formErrors.firstName}
                helperText={formErrors.firstName}
                size="small"
                autoFocus
                disabled={formData.relation === RELATIONS.SELF}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Last Name"
                value={formData.lastName}
                onChange={(e) => handleInputChange('lastName', e.target.value)}
                error={!!formErrors.lastName}
                helperText={formErrors.lastName}
                size="small"
                disabled={formData.relation === RELATIONS.SELF}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Age"
                type="number"
                value={formData.age}
                onChange={(e) => handleInputChange('age', e.target.value)}
                error={!!formErrors.age}
                helperText={formErrors.age}
                size="small"
                inputProps={{ min: 0, max: 150 }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              {formData.relation !== RELATIONS.SELF && (
                <FormControl fullWidth size="small">
                  <InputLabel>Relationship</InputLabel>
                  <Select
                    value={formData.relation}
                    onChange={(e) => handleInputChange('relation', e.target.value)}
                    label="Relationship"
                    disabled={formData.relation === RELATIONS.SELF}
                  >
                    {Object.values(RELATIONS_OPTIONS).map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Phone Number"
                value={formData.phoneNumber}
                onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
                size="small"
                placeholder="Optional phone number"
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, gap: 1, flexWrap: 'wrap' }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Sports & Skill Levels
                </Typography>
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  variant="outlined"
                  onClick={() => {
                    const available = Object.values(SPORTS_OPTIONS).filter((opt) => !(formData.sports || []).some((s) => s.name === opt.value))
                    setNewSportValue(available[0]?.value || '')
                    setNewSportLevel(SKILL_LEVELS.BEGINNER)
                    setIsAddSportOpen(true)
                  }}
                >
                  Add Sport
                </Button>
              </Box>
              {(formData.sports || []).length > 0 && (
                <Stack spacing={1}>
                  {(formData.sports || []).map((sport, index) => {
                    const meta = Object.values(SPORTS_OPTIONS).find((opt) => opt.value === sport.name)
                    const levelDesc = (SKILL_LEVEL_TOOLTIP[sport.name] && SKILL_LEVEL_TOOLTIP[sport.name][sport.skillLevel]?.tooltip) || ''
                    return (
                      <Paper key={index} sx={{ p: 1.5 }} variant="outlined">
                        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'stretch', sm: 'center' }, gap: 1.5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: { sm: 160 } }}>
                            <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>{meta?.icon}</Box>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {meta?.label || sport.name}
                            </Typography>
                          </Box>
                          <FormControl size="small" sx={{ minWidth: { sm: 160 }, width: { xs: '100%', sm: 'auto' } }}>
                            <InputLabel>Skill Level</InputLabel>
                            <Select value={sport.skillLevel} onChange={(e) => handleSkillLevelChange(sport.name, e.target.value)} label="Skill Level">
                              {Object.values(SKILL_LEVELS_OPTIONS).map((level) => (
                                <MenuItem key={level.value} value={level.value}>
                                  {level.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          <Box sx={{ flex: 1, width: { xs: '100%', sm: 'auto' } }}>
                            {!!levelDesc && (
                              <Typography variant="caption" color="text.primary" sx={{ display: 'block', whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                {levelDesc}
                              </Typography>
                            )}
                          </Box>
                          <Box sx={{ alignSelf: { xs: 'flex-end', sm: 'auto' } }}>
                            <IconButton size="small" color="error" onClick={() => handleRemoveSport(sport.name)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>
                      </Paper>
                    )
                  })}
                </Stack>
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">
            {editingIndex >= 0 ? 'Save Changes' : 'Add Member'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Sport Dialog */}
      <Dialog open={isAddSportOpen} onClose={() => setIsAddSportOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Sport</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={{ xs: 12 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Sport</InputLabel>
                <Select value={newSportValue} onChange={(e) => setNewSportValue(e.target.value)} label="Sport">
                  {Object.values(SPORTS_OPTIONS)
                    .filter((opt) => !(formData.sports || []).some((s) => s.name === opt.value))
                    .map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>{opt.icon}</Box>
                          <Typography variant="body2">{opt.label}</Typography>
                        </Box>
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Skill Level</InputLabel>
                <Select value={newSportLevel} onChange={(e) => setNewSportLevel(e.target.value)} label="Skill Level">
                  {Object.values(SKILL_LEVELS_OPTIONS).map((level) => (
                    <MenuItem key={level.value} value={level.value}>
                      {level.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12 }}>
              {!!newSportValue && (
                <Alert severity="info" sx={{ py: 0.5, px: 1, '& .MuiAlert-message': { p: 0, m: 0 } }}>
                  <Typography variant="body2" color="text.primary" sx={{ display: 'block', whiteSpace: 'normal', wordBreak: 'break-word' }}>
                    {(SKILL_LEVEL_TOOLTIP[newSportValue] && SKILL_LEVEL_TOOLTIP[newSportValue][newSportLevel]?.tooltip) || ''}
                  </Typography>
                </Alert>
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsAddSportOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!newSportValue}
            onClick={() => {
              if (!newSportValue) return
              setFormData((prev) => ({
                ...prev,
                sports: [...(prev.sports || []), { name: newSportValue, skillLevel: newSportLevel }],
              }))
              setIsAddSportOpen(false)
            }}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default FamilyMemberForm
